import { getRemoHosts } from "config";
import companyApi from "modules/company/company.api";
import { selectEventCompanyId } from "modules/event/selectors";
import { Errors, trackError } from "modules/monitoring";
import useQueryParams from "modules/queryParams/hooks/useQueryParams";
import {
  selectWhiteLabelFaviconURL,
  selectEnableAgencyWhiteLabelling,
} from "modules/settings/redux/selectors";
import { setSettings } from "modules/settings/redux/slice";
import { useEffect } from "react";
import Favicon from "react-favicon";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import {
  getCompanyPublicSettingsByCustomDomain,
  getCompanyPublicSettingsByEventCode,
} from "services/apiService/apis";
import { useAppDispatch } from "store/hooks";

interface Props {
  setIsLoading?: (isLoading: boolean) => void;
}

export const WhiteLabelSettingsLoader = ({ setIsLoading }: Props) => {
  const dispatch = useAppDispatch();
  const { event: eventCodeQueryParam } = useQueryParams();
  const companyId = useSelector(selectEventCompanyId);
  const params = useParams<{ eventCode?: string }>();
  const storedEnableAgencyWhiteLabelling = useSelector(
    selectEnableAgencyWhiteLabelling,
  );
  const storedWhiteLabelFaviconURL = useSelector(selectWhiteLabelFaviconURL);
  const { eventCode: eventCodeParam } = params;
  const isHostnameCustomDomain = !getRemoHosts().includes(window.location.host);

  const fetchWhiteLabelSettings = async () => {
    try {
      if (companyId) {
        setIsLoading?.(true);
        const {
          companySettings: {
            enableAgencyWhiteLabelling,
            whiteLabelFaviconURL,
            whiteLabelLogoURL,
            whiteLabelSignInBackgroundURL,
            disableFreshchatWidget,
          },
        } = await companyApi.getCompanyPublicSettings(companyId);

        dispatch(
          setSettings({
            enableAgencyWhiteLabelling: enableAgencyWhiteLabelling ?? false,
            whiteLabelFaviconURL,
            whiteLabelLogoURL,
            whiteLabelSignInBackgroundURL,
            disableFreshchatWidget,
          }),
        );
        setIsLoading?.(false);
        return;
      }

      // when /mic-cam-check page is loaded manually we only have event code in query params
      if (eventCodeQueryParam) {
        setIsLoading?.(true);
        const {
          enableAgencyWhiteLabelling,
          whiteLabelFaviconURL,
          whiteLabelLogoURL,
          whiteLabelSignInBackgroundURL,
          disableFreshchatWidget,
        } = await getCompanyPublicSettingsByEventCode(
          eventCodeQueryParam as string,
        );

        dispatch(
          setSettings({
            enableAgencyWhiteLabelling: enableAgencyWhiteLabelling ?? false,
            whiteLabelFaviconURL,
            whiteLabelLogoURL,
            whiteLabelSignInBackgroundURL,
            disableFreshchatWidget,
          }),
        );
        setIsLoading?.(false);
        return;
      }

      // landing page
      if (eventCodeParam) {
        setIsLoading?.(true);
        const {
          enableAgencyWhiteLabelling,
          whiteLabelFaviconURL,
          whiteLabelLogoURL,
          whiteLabelSignInBackgroundURL,
          disableFreshchatWidget,
        } = await getCompanyPublicSettingsByEventCode(eventCodeParam as string);

        dispatch(
          setSettings({
            enableAgencyWhiteLabelling: enableAgencyWhiteLabelling ?? false,
            whiteLabelFaviconURL,
            whiteLabelLogoURL,
            whiteLabelSignInBackgroundURL,
            disableFreshchatWidget,
          }),
        );
        setIsLoading?.(false);
        return;
      }

      // fallback - mostly for auth pages, my-events
      if (isHostnameCustomDomain) {
        setIsLoading?.(true);
        const {
          company,
          enableAgencyWhiteLabelling,
          whiteLabelFaviconURL,
          whiteLabelLogoURL,
          whiteLabelSignInBackgroundURL,
          disableFreshchatWidget,
        } = await getCompanyPublicSettingsByCustomDomain(window.location.host);

        dispatch(
          setSettings({
            customDomainCompany: company,
            enableAgencyWhiteLabelling: enableAgencyWhiteLabelling ?? false,
            whiteLabelFaviconURL,
            whiteLabelLogoURL,
            whiteLabelSignInBackgroundURL,
            disableFreshchatWidget,
          }),
        );
        setIsLoading?.(false);
        return;
      }
    } catch (error) {
      trackError(error, {
        label: Errors.AGENCY_WHITE_LABEL_SETTINGS,
      });
    }
  };

  useEffect(() => {
    fetchWhiteLabelSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, eventCodeParam, eventCodeQueryParam, isHostnameCustomDomain]);

  if (storedEnableAgencyWhiteLabelling && storedWhiteLabelFaviconURL) {
    return <Favicon url={storedWhiteLabelFaviconURL} />;
  }

  return null;
};
